<script setup lang="ts">
import type { VCheckboxTileProps } from "./VCheckboxTile.types";
import { createReusableTemplate } from "@vueuse/core";
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, useId } from "vue";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import {
  choiceTileBodyCva,
  choiceTileCva,
  choiceTileSceneCva,
  choiceTileStackedCva,
  choiceTileSubtitleCva,
  choiceTileTitleCva,
} from "../ChoiceTile/choiceTile.cva";
import { checkboxTileBoxCva } from "./VCheckboxTile.cva";
import { CHECKBOX_TILE_GROUP_KEY } from "./VCheckboxTile.types";

defineOptions({
  name: "VCheckboxTile",
  inheritAttrs: false,
});

const {
  value,
  title,
  subtitle = "",
  disabled = false,
  disabledReason = undefined,
  size = "md",
  testId = undefined,
} = defineProps<VCheckboxTileProps>();

defineSlots<{
  /**
   * The scene: 80x56 on the left of a `row` tile, centred in a 64px box over the full width of a `stacked` tile (an
   * illustration from the App Store illustration set). It is hidden from screen readers; the title names the option.
   */
  illustration?: () => unknown
  /** Extra content under the subtitle, e.g. a count or a chip. */
  default?: () => unknown
}>();

const group = inject(CHECKBOX_TILE_GROUP_KEY, null);

if (!group) {
  throw new Error("VCheckboxTile must be used inside a VCheckboxTileGroup");
}

const attrs = useAttrs();
// `offset` drops the box 2px, onto the first 20px line of the title of a stacked tile.
const [DefineCheckbox, ReuseCheckbox] = createReusableTemplate<{ offset: boolean }>();
const [DefineTile, ReuseTile] = createReusableTemplate();
const inputRef = ref<HTMLInputElement | null>(null);
const reasonId = useId();

const reason = computed(() => {
  const trimmed = disabledReason?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
});
// A tile with a reason is locked: it keeps its state and stays focusable, so the tooltip and the screen reader can
// explain why. A plainly disabled tile is a native disabled checkbox, out of the tab order like VRadioTile.
const isLocked = computed(() => reason.value !== null);
const isDisabled = computed(() => disabled || group.disabled.value);
const isInactive = computed(() => isDisabled.value || isLocked.value);
const isChecked = computed(() => group.modelValue.value.includes(value));
// Only a tile the user can change turns red; a locked tile cannot fix the selection.
const isInvalid = computed(() => group.invalid.value && !isInactive.value);

const stacked = computed(() => group.variant.value === "stacked");
const tileClass = computed(() => stacked.value
  ? choiceTileStackedCva({ selected: isChecked.value, disabled: isInactive.value, invalid: isInvalid.value })
  : choiceTileCva({ selected: isChecked.value, disabled: isInactive.value, invalid: isInvalid.value, size }));
const titleClass = computed(() =>
  choiceTileTitleCva({ disabled: isInactive.value, size, variant: group.variant.value }));
const subtitleClass = computed(() => choiceTileSubtitleCva({ size, variant: group.variant.value }));
const bodyClass = computed(() => choiceTileBodyCva({ size }));
const sceneClass = computed(() => choiceTileSceneCva({ selected: isChecked.value }));
const boxClass = computed(() =>
  checkboxTileBoxCva({ checked: isChecked.value, disabled: isInactive.value, invalid: isInvalid.value }));

const describedBy = computed(() => {
  const ids = [
    isLocked.value ? reasonId : null,
    group.invalid.value ? group.errorId.value : null,
  ].filter((id): id is string => Boolean(id));
  return ids.length > 0 ? ids.join(" ") : undefined;
});

// Without a tooltip the tile itself is the root and takes every attribute (such as `class="h-full"`); with one,
// the tooltip wrapper takes the class so the tile still fills its grid cell.
const tileAttrs = computed(() => {
  const { class: _class, ...rest } = attrs;
  return rest;
});

let unregister: (() => void) | null = null;
onMounted(() => {
  unregister = group.register({ value: () => value, element: () => inputRef.value });
});
onBeforeUnmount(() => {
  unregister?.();
});

function onClick(event: MouseEvent) {
  if (isLocked.value) {
    event.preventDefault();
  }
}

async function onChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (isInactive.value) {
    input.checked = isChecked.value;
    return;
  }
  group?.toggle(value, input.checked);
  // Controlled use: a parent that does not take the change keeps its value, so the box follows the model again.
  await nextTick();
  if (inputRef.value) {
    inputRef.value.checked = isChecked.value;
  }
}
</script>

<template>
  <!-- The native checkbox, visually hidden, and the box drawn for it; the tile label around it toggles it. -->
  <DefineCheckbox v-slot="{ offset }">
    <span class="relative flex shrink-0" :class="{ 'mt-0.5': offset }">
      <input
        ref="inputRef"
        type="checkbox"
        class="peer sr-only"
        :name="group.name.value"
        :value="String(value)"
        :checked="isChecked"
        :disabled="isDisabled"
        :aria-disabled="isLocked || undefined"
        :aria-invalid="isInvalid || undefined"
        :aria-describedby="describedBy"
        data-checkbox-tile-input
        @click="onClick"
        @change="onChange"
      >
      <span :class="boxClass" aria-hidden="true">
        <svg
          v-if="isChecked"
          width="8"
          height="6.4"
          viewBox="0 0 10 8"
          fill="none"
          focusable="false"
        >
          <path d="M1 4l2.5 2.5L9 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </span>
  </DefineCheckbox>

  <DefineTile>
    <label
      v-if="stacked"
      :class="[tileClass, reason ? 'min-w-0 flex-1' : attrs.class]"
      :aria-disabled="isInactive || undefined"
      :data-state="isChecked ? 'checked' : 'unchecked'"
      :data-testid="testId"
      data-variant="stacked"
      v-bind="tileAttrs"
    >
      <span v-if="$slots.illustration" :class="sceneClass" aria-hidden="true" data-testid="checkbox-tile-scene">
        <slot name="illustration" />
      </span>
      <!-- The checkbox sits left, its 16px box centred on the first 20px line of the title. -->
      <span class="flex items-start gap-2.5">
        <ReuseCheckbox :offset="true" />
        <span :class="bodyClass">
          <span :class="titleClass">{{ title }}</span>
          <span v-if="subtitle" :class="subtitleClass">{{ subtitle }}</span>
          <slot />
        </span>
      </span>
    </label>
    <label
      v-else
      :class="[tileClass, reason ? 'min-w-0 flex-1' : attrs.class]"
      :aria-disabled="isInactive || undefined"
      :data-state="isChecked ? 'checked' : 'unchecked'"
      :data-testid="testId"
      v-bind="tileAttrs"
    >
      <span v-if="$slots.illustration" class="flex shrink-0" aria-hidden="true">
        <slot name="illustration" />
      </span>
      <span :class="bodyClass">
        <span :class="titleClass">{{ title }}</span>
        <span v-if="subtitle" :class="subtitleClass">{{ subtitle }}</span>
        <slot />
      </span>
      <ReuseCheckbox :offset="false" />
    </label>
  </DefineTile>

  <ACTooltip
    v-if="reason"
    allow-change
    :disabled-reason="reason"
    class="flex"
    :class="[attrs.class]"
  >
    <ReuseTile />
    <span :id="reasonId" class="sr-only">{{ reason }}</span>
  </ACTooltip>
  <ReuseTile v-else />
</template>
